import { Badge, Box, Button, Divider, Drawer, Select, Typography } from '@robocorp/components';
import { IconFileText } from '@robocorp/icons/iconic';
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, matchPath } from 'react-router-dom';
import { DocsLinks } from '../../../constants';
import { useSelector } from '../../../hooks';
import { useCurrentOrganization } from '../../../queries';
import CodeEditor from '../../CodeEditor';
import { ApiOption, apiOptions } from './ApiEndpoints';
import {
  ApiHelperInput,
  SelectApiKey,
  SelectArtifactId,
  SelectAssistantId,
  SelectProcessId,
  SelectProcessRun,
  SelectRobotId,
  SelectWorkItem,
  SelectWorkItemFile,
  SelectRobotRunId,
  SelectEnvironment,
  SelectEnvironmentGroup,
  SelectEnvironmentGroupLinkToken,
  Params,
} from './components';
import { VariablesContext } from './context';
import { useVariables } from './hooks/useVariables';

/**
 * Displays a Robocorp API helper sidebar with predefined steps to create ready-to-use API calls and curl commands
 *
 * @example
 * <ApiHelper />
 */

const ApiHelper = () => {
  const [endpoint, setEndpoint] = useState<undefined | ApiOption>(undefined);

  const [payload, setPayload] = useState<undefined | string>(undefined);
  const [variables, setVariables] = useVariables();
  const [params, setParams] = useState<string>('');

  const { t } = useTranslation();
  const location = useLocation();

  const currentWorkspaceId = useSelector((state) => state.ui.currentWorkspaceId);

  const { data: currentOrganization } = useCurrentOrganization();

  /**
   * Presets default API endpoint variables based on the current active route
   * */

  useEffect(() => {
    const processId = matchPath(
      '/:organizationShortName/:workspaceShortName/processes/:processId/*',
      location.pathname,
    )?.params?.processId;
    const processRunId = matchPath(
      '/:organizationShortName/:workspaceShortName/processes/:processId/:processRunId/*',
      location.pathname,
    )?.params?.processRunId;
    const robotRunId = matchPath(
      '/:organizationShortName/:workspaceShortName/processes/:processId/:processRunId/:robotRunId',
      location.pathname,
    )?.params?.robotRunId;
    const assistantId = matchPath(
      '/:organizationShortName/:workspaceShortName/assistants/:assistantId',
      location.pathname,
    )?.params?.assistantId;
    const robotId = matchPath(
      '/:organizationShortName/:workspaceShortName/robots/:robotId',
      location.pathname,
    )?.params?.robotId;
    const workItemId = matchPath(
      '/:organizationShortName/:workspaceShortName/processes/:processId/workItems/:workItemId',
      location.pathname,
    )?.params?.workItemId;
    const environmentId = matchPath(
      '/:organizationShortName/:workspaceShortName/workers/:environmentId',
      location.pathname,
    )?.params?.environmentId;
    const environmentGroupId = matchPath(
      '/:organizationShortName/:workspaceShortName/workers/groups/:environmentGroupId',
      location.pathname,
    )?.params?.environmentGroupId;

    setVariables({
      assistantId,
      robotId,
      processId,
      processRunId,
      robotRunId,
      workItemId,
      environmentId,
      environmentGroupId,
    });
  }, [location]);

  /**
   * Maps predefined API options to be used in a `Select` component
   */
  const endpointsItems = useMemo(
    () =>
      Object.values(apiOptions).flatMap((item) =>
        item.options
          .map((option) => {
            if (
              (!currentOrganization?.limit_overrides.processApiEnabled &&
                option.permission !== 'trigger_processes') ||
              currentOrganization?.limit_overrides.processApiEnabled
            ) {
              return {
                optgroup: t(item.translationKey),
                label: t(option.translationKey),
                value: option.translationKey,
                badge: option.new
                  ? {
                      label: t('new'),
                      variant: 'info' as const,
                    }
                  : undefined,
              };
            }
            return { optgroup: '', label: '', value: '' };
          })
          .sort((a, b) => a.label.localeCompare(b.label)),
      ),
    [apiOptions],
  );

  const onSelectEndpoint = useCallback(
    (target: string) => {
      const option = Object.values(apiOptions)
        .flatMap((item) => item.options)
        .find((item) => item.translationKey === target);

      setEndpoint(option);
      setPayload(option?.optionalData);
    },
    [endpoint, setEndpoint],
  );

  /**
   * Creates a plain text curl command based on selected API options
   */
  const exampleCommand = useMemo(() => {
    if (!endpoint?.endpoint) {
      return '';
    }
    const server = endpoint.serverOverride ?? apiOptions[endpoint.api].server;

    let command = `curl --request ${endpoint?.method} "${server}/workspaces/${currentWorkspaceId}${
      endpoint.endpoint
    }${params}" --header "Authorization: RC-WSKEY $apiKey" ${
      payload ? `--data ${JSON.stringify(payload).replace(/(?:\r\n|\r|\n)/g, '')}` : ''
    }`;

    command = Object.entries(variables).reduce(
      (final, [replacement, value]) =>
        final.replaceAll(`$${replacement}`, value || `$${replacement}`),
      command,
    );

    return command;
  }, [currentWorkspaceId, endpoint, variables, params, payload]);

  return (
    <>
      <Drawer.Header>
        <Drawer.Header.Title title={t('APIHelper.title')}>
          <Badge label={t('APIHelper.labelOld')} size="small" variant="danger" />
        </Drawer.Header.Title>
        <Drawer.Header.Description>
          <Button
            forwardedAs="a"
            href={DocsLinks.WORKSPACE_API}
            target="_blank"
            icon={IconFileText}
            variant="secondary"
          >
            {t('seeRobocorpDocs')}
          </Button>
          <Typography color="grey20" mt={12} mb={18}>
            {t('APIHelper.oldDescription')}
          </Typography>
        </Drawer.Header.Description>
      </Drawer.Header>
      <Box m={30} mt={20}>
        <Box mt={18} mb={20}>
          <Select
            label={t('selectApiOptionPrompt')}
            items={endpointsItems}
            onChange={onSelectEndpoint}
            value={endpoint?.translationKey}
            placeholder={`${t('choose')}...`}
            searchable
          />
        </Box>

        {!endpoint ? (
          <Typography as="h3" mt={20} mb={20}>
            {t('methodNotChosen')}
          </Typography>
        ) : (
          <>
            <VariablesContext.Provider
              value={{
                endpoint: `${endpoint.endpoint}${endpoint.optionalData}`,
                permission: endpoint.permission as any,
                variables,
                setVariables,
              }}
            >
              <SelectProcessId />
              <SelectProcessRun workspaceId={currentWorkspaceId} />
              <SelectRobotId />
              <SelectRobotRunId />
              <SelectArtifactId />
              <SelectAssistantId />
              <SelectWorkItem />
              <SelectWorkItemFile />
              <SelectApiKey />
              <SelectEnvironment />
              <SelectEnvironmentGroup />
              <SelectEnvironmentGroupLinkToken />

              {endpoint.customData && (
                <Box mb={20}>
                  <Typography mb={8}>{t('payload')}</Typography>
                  <Box height={120}>
                    <CodeEditor
                      mode="json"
                      value={payload || ''}
                      onChange={setPayload}
                      lineNumbers={false}
                      lineWrapping
                    />
                  </Box>
                </Box>
              )}
            </VariablesContext.Provider>

            <Params params={endpoint?.params || []} onChange={setParams} />

            <Divider />

            <Box display="flex" alignItems="center">
              <Typography as="h3" mt={20} mb={20} mr={16}>
                {t(endpoint.translationKey)}
              </Typography>
              {endpoint && <Badge label={endpoint.method} size="small" variant="primary" />}
            </Box>

            <ApiHelperInput
              label={t('server')}
              value={endpoint ? (endpoint.serverOverride ?? apiOptions[endpoint.api].server) : ''}
            />
            <ApiHelperInput label={t('workspaceId')} value={currentWorkspaceId} />

            {Object.keys(variables).map(
              (variable) =>
                endpoint?.endpoint.includes(variable) && (
                  <ApiHelperInput key={variable} label={t(variable)} value={variables[variable]} />
                ),
            )}

            <Divider />

            <Typography mt={20} mb={8}>
              {t('generatedApiCurlDesc')}
            </Typography>

            <Box>
              <CodeEditor
                mode="sh"
                value={exampleCommand}
                lineNumbers={false}
                copyToClipboard
                lineWrapping
                readOnly
              />
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

interface Props {
  open: boolean;
  onClose: () => void;
  children?: ReactNode;
}

export const OldApiHelper: FC<Props> = ({ open, onClose, children }) => {
  return (
    <Drawer open={open} onClose={onClose} width={580}>
      <ApiHelper />
      {children}
    </Drawer>
  );
};
